import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RacePlayer from './RacePlayer';
import { drawBackground, THEMES, type Theme } from '../game/levels';
import {
  blankRace, bump, checkRace, cleanRace, drawGround, drawRaceItem, drawTerrain, groundAt, pit, ramp, RACE_LIMITS,
  resizeRace, setItem, smooth, type RaceLevel,
} from '../game/race';
import { MAKE_COST } from '../lib/shop';
import { useServerDraft } from './useServerDraft';
import SendChecklist from './SendChecklist';

const BLANK_RACE = blankRace();

// ─────────────────────────────────────────────────────────────────────────────
// The race course maker: shape the ground (draw it, add hills, valleys, jump
// ramps and pits, smooth it), put coins, boost pads, checkpoints and the
// finish along it, then drive it yourself. Like the level maker, you have to
// finish your own course before you can send it, and the draft stays in this
// browser while you work.
// ─────────────────────────────────────────────────────────────────────────────

const DRAFT_KEY = 'race-maker-draft';
const TS = 18;   // px per tile in the editor

type Tool = 'draw' | 'hill' | 'valley' | 'ramp' | 'pit' | 'smooth' | 'o' | 'z' | 'h' | 'G' | 'erase';
const TOOLS: { id: Tool; icon: string; name: string; tip: string; drag: boolean }[] = [
  { id: 'draw', icon: '✏️', name: 'Draw ground', tip: 'Drag to draw where the ground goes', drag: true },
  { id: 'hill', icon: '⛰️', name: 'Hill', tip: 'Click to raise a smooth hill', drag: false },
  { id: 'valley', icon: '🏞️', name: 'Valley', tip: 'Click to dig a smooth valley', drag: false },
  { id: 'ramp', icon: '📐', name: 'Ramp', tip: 'Click to add a jump ramp (it drops off at the end)', drag: false },
  { id: 'pit', icon: '🕳️', name: 'Pit', tip: 'Click to cut a gap: jump it or crash', drag: false },
  { id: 'smooth', icon: '〰️', name: 'Smooth', tip: 'Drag to smooth bumps (and fill in pits)', drag: true },
  { id: 'o', icon: '🪙', name: 'Coin', tip: 'Click or drag to place coins', drag: true },
  { id: 'z', icon: '⚡', name: 'Boost', tip: 'A pad that speeds you up', drag: false },
  { id: 'h', icon: '🚩', name: 'Checkpoint', tip: 'You come back here after a crash', drag: false },
  { id: 'G', icon: '🏁', name: 'Finish', tip: 'Cross it to win', drag: false },
  { id: 'erase', icon: '🧽', name: 'Remove', tip: 'Drag to remove coins, boosts, checkpoints and finish lines', drag: true },
];

function loadDraft(): { title: string; level: RaceLevel; local: boolean } {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY) ?? 'null');
    const level = cleanRace(d?.level);
    if (level) return { title: String(d.title ?? '').slice(0, 60), level, local: true };
  } catch { /* fresh start */ }
  return { title: '', level: blankRace(), local: false };
}

type Props = { balance: number; look: unknown; onPublish: (title: string, level: RaceLevel) => Promise<void> };

export default function RaceEditor({ balance, look, onPublish }: Props) {
  const first = useMemo(loadDraft, []);
  const [title, setTitle] = useState(first.title);
  const [level, setLevel] = useState<RaceLevel>(first.level);
  useServerDraft('race', title, level, BLANK_RACE, first.local, cleanRace, (t, l) => { setTitle(t); setLevel(l); });
  const [tool, setTool] = useState<Tool>('draw');
  const [past, setPast] = useState<RaceLevel[]>([]);
  const [testing, setTesting] = useState(false);
  const [beaten, setBeaten] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [hoverX, setHoverX] = useState<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const levelRef = useRef(level);
  levelRef.current = level;
  const dragging = useRef<RaceLevel | null>(null);

  const asJson = JSON.stringify(level);
  const problem = checkRace(level);
  const ready = !problem && beaten === asJson;
  const H = (level.h + 3) * TS;

  useEffect(() => { try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ title, level })); } catch { /* fine */ } }, [title, level]);

  // Draw the course.
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const W = level.w * TS;
    c.width = W * dpr; c.height = H * dpr; c.style.width = `${W}px`; c.style.height = `${H}px`;
    const g = c.getContext('2d')!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawBackground(g, level.theme, W, H, 0, 0);
    drawTerrain(g, level, TS, 0, 0, W, H);
    g.fillStyle = 'rgba(30,38,51,0.35)'; g.font = '10px system-ui'; g.textAlign = 'center';
    for (let x = 0; x <= level.w; x += 10) { g.fillRect(x * TS, H - 8, 1, 8); g.fillText(String(x), x * TS, H - 10); }
    for (let i = 0; i < level.w; i++) {
      const ch = level.items[i]; if (ch === '.') continue;
      const gy = groundAt(level, i + 0.5); if (gy == null) continue;
      drawRaceItem(g, ch, (i + 0.5) * TS, gy * TS, TS, 0.3);
    }
    // The car's start.
    const sy = groundAt(level, 3);
    if (sy != null) { g.fillStyle = 'rgba(47,207,106,0.8)'; g.font = `bold ${TS}px system-ui`; g.fillText('▶', 3 * TS, sy * TS - TS * 0.6); }
    if (hoverX != null) { g.fillStyle = 'rgba(255,255,255,0.3)'; g.fillRect(Math.floor(hoverX) * TS, 0, TS, H); }
  }, [level, hoverX, H, testing]);

  const commit = useCallback((before: RaceLevel) => { setPast(p => [...p.slice(-60), before]); }, []);
  const undo = useCallback(() => setPast(p => { if (!p.length) return p; setLevel(p[p.length - 1]); return p.slice(0, -1); }), []);
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (testing || !(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z' || (e.target as HTMLElement)?.tagName === 'INPUT') return;
      e.preventDefault(); undo();
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [undo, testing]);

  const at = (e: React.PointerEvent) => { const r = canvasRef.current!.getBoundingClientRect(); return { x: (e.clientX - r.left) / TS, y: (e.clientY - r.top) / TS }; };
  const apply = (l: RaceLevel, x: number, y: number, firstClick: boolean): RaceLevel => {
    switch (tool) {
      case 'draw': return drawGround(l, x, y);
      case 'smooth': return smooth(l, x);
      case 'hill': return firstClick ? bump(l, x, 2) : l;
      case 'valley': return firstClick ? bump(l, x, -2) : l;
      case 'ramp': return firstClick ? ramp(l, x) : l;
      case 'pit': return firstClick ? pit(l, x) : l;
      case 'erase': return setItem(l, x, '.');
      case 'G': return firstClick ? setItem({ ...l, items: l.items.replace(/G/g, '.') }, x, 'G') : l;   // one finish line
      default: return firstClick || tool === 'o' ? setItem(l, x, tool) : l;
    }
  };

  if (testing) return (
    <RacePlayer level={level} look={look} title={title || 'My race'}
      onDone={r => { if (r.won) setBeaten(asJson); }}
      footer={beaten === asJson ? <p className="notice good">You finished your course, so you can send it in! 🎉</p> : <p className="small muted">Finish your own course to be able to send it in.</p>}
      onClose={() => setTesting(false)} />
  );

  const publish = async () => {
    if (!title.trim()) { setError('Give your race a name.'); return; }
    if (problem) { setError(problem); return; }
    if (!ready) { setError('Drive your course to the finish first.'); return; }
    setBusy(true); setError('');
    try { await onPublish(title.trim(), level); setTitle(''); setLevel(blankRace()); setPast([]); setBeaten(''); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not send your race.'); }
    setBusy(false);
  };

  const current = TOOLS.find(t => t.id === tool)!;
  return (
    <div className="level-editor">
      <div className="panel level-settings">
        <label className="field">
          <span>Name</span>
          <input value={title} maxLength={60} onChange={e => setTitle(e.target.value)} placeholder="Bumpy Hills Rally" />
        </label>
        <div className="level-options">
          <label>World
            <select value={level.theme} onChange={e => { commit(level); setLevel({ ...level, theme: e.target.value as Theme }); }}>
              {(Object.keys(THEMES) as Theme[]).map(t => <option key={t} value={t}>{THEMES[t].name}</option>)}
            </select>
          </label>
          <label>Time limit
            <select value={level.time} onChange={e => { commit(level); setLevel({ ...level, time: Number(e.target.value) }); }}>
              <option value={0}>None</option>
              {[30, 60, 90, 120, 180, 300].map(n => <option key={n} value={n}>{n < 120 ? `${n} seconds` : `${n / 60} minutes`}</option>)}
            </select>
          </label>
          <span className="level-size">
            Length {level.w}
            <button type="button" className="small" disabled={level.w <= RACE_LIMITS.minW} onClick={() => { commit(level); setLevel(resizeRace(level, level.w - 50)); }}>− shorter</button>
            <button type="button" className="small" disabled={level.w >= RACE_LIMITS.maxW} onClick={() => { commit(level); setLevel(resizeRace(level, level.w + 50)); }}>+ longer</button>
          </span>
        </div>
      </div>

      <div className="level-palette" role="radiogroup" aria-label="Tool">
        <div className="pal-group">
          <span className="pal-label">Ground</span>
          {TOOLS.slice(0, 6).map(t => (
            <button key={t.id} type="button" role="radio" aria-checked={tool === t.id} className={`pal-tile ${tool === t.id ? 'on' : ''}`} onClick={() => setTool(t.id)} title={t.tip}>
              <span className="pal-eraser" aria-hidden>{t.icon}</span><span>{t.name}</span>
            </button>
          ))}
        </div>
        <div className="pal-group">
          <span className="pal-label">Things</span>
          {TOOLS.slice(6).map(t => (
            <button key={t.id} type="button" role="radio" aria-checked={tool === t.id} className={`pal-tile ${tool === t.id ? 'on' : ''}`} onClick={() => setTool(t.id)} title={t.tip}>
              <span className="pal-eraser" aria-hidden>{t.icon}</span><span>{t.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="level-toolbar row">
        <button type="button" className="small" disabled={!past.length} onClick={undo} title="Undo (Ctrl+Z)">↶ Undo</button>
        <button type="button" className="small" onClick={() => { if (confirm('Start over with a new course?')) { commit(level); setLevel(blankRace()); } }}>🗑 Start over</button>
        <span className="spacer" />
        <span className="small muted">{current.tip}</span>
      </div>

      <div className="level-grid-wrap">
        <canvas ref={canvasRef} className="level-grid"
          onPointerDown={e => {
            e.preventDefault();
            canvasRef.current!.setPointerCapture(e.pointerId);
            dragging.current = levelRef.current;
            const { x, y } = at(e);
            setLevel(l => apply(l, x, y, true));
          }}
          onPointerMove={e => {
            const { x, y } = at(e);
            setHoverX(h => (h != null && Math.floor(h) === Math.floor(x) ? h : x));
            if (dragging.current && current.drag) setLevel(l => apply(l, x, y, false));
          }}
          onPointerUp={() => { const before = dragging.current; dragging.current = null; if (before && before !== levelRef.current) commit(before); }}
          onPointerLeave={() => setHoverX(null)}
          onContextMenu={e => e.preventDefault()} />
      </div>

      {problem && <div className="notice">{problem}</div>}
      {error && <div className="notice bad" role="alert">{error}</div>}

      <div className="panel level-publish">
        <div className="row maker-actions">
          <SendChecklist named={!!title.trim()} problem={problem} beaten={beaten === asJson} balance={balance} verb="finish" />
          <button type="button" disabled={!!problem} onClick={() => { setError(''); setTesting(true); }}>🏎️ Drive it</button>
          <button type="button" className="primary" disabled={busy || !ready || !title.trim() || balance < MAKE_COST} onClick={publish}>
            {busy ? 'Sending…' : `Send to my teacher · 🪙 ${MAKE_COST}`}
          </button>
        </div>
        <p className="small muted">Your teacher checks every game and sets its price to play. The 🪙 {MAKE_COST} isn't given back, even if your teacher says no. Your course is saved as you work, and your teacher can see it.</p>
      </div>
    </div>
  );
}
